import { AlertTriangle, CheckCircle2, Copy, Info, Loader2 } from 'lucide-react';
import { Toaster as Sonner, toast } from 'sonner';
import i18n from '../../app/i18n';
import { useTheme } from '../../theme/ThemeProvider';
import { THEMES } from '../../theme/themes';
import { ApiError } from '../../lib/api';

// Toasts for API actions (§17a): promise toasts (loading → done in one), friendly EN/FR
// messages from API error codes, and "Copy reference" with the request id so support can find
// the exact request. Raw error text never reaches the user.

export function Toaster() {
  const { resolved } = useTheme();
  return (
    <Sonner
      theme={THEMES[resolved].scheme}
      position="bottom-right"
      gap={10}
      offset={20}
      visibleToasts={4}
      closeButton
      icons={{
        success: <CheckCircle2 size={18} className="text-success" />,
        error: <AlertTriangle size={18} className="text-danger" />,
        info: <Info size={18} className="text-info" />,
        loading: <Loader2 size={18} className="animate-spin text-primary" />,
      }}
      toastOptions={{
        classNames: {
          toast:
            '!rounded-lg !border !border-border !bg-surface !text-text !shadow-[0_18px_40px_-18px_rgb(0_0_0/0.45)] !font-sans',
          title: '!font-semibold !text-base',
          description: '!text-muted !text-sm',
          actionButton: '!bg-primary !text-on-primary !font-semibold !rounded-sm',
          cancelButton: '!bg-subtle !text-text !rounded-sm',
          closeButton: '!bg-surface !border-border !text-muted',
        },
      }}
    />
  );
}

/** Friendly, translated message for any error thrown by the API client. */
export function errorMessage(err: unknown): string {
  const t = i18n.t.bind(i18n);
  if (err instanceof ApiError) {
    const key = `errors.${err.code}`;
    return i18n.exists(key)
      ? t(key)
      : t(err.status >= 500 ? 'errors.internal' : 'errors.bad_request');
  }
  return t('errors.network');
}

export function toastError(err: unknown): void {
  const t = i18n.t.bind(i18n);
  const requestId = err instanceof ApiError ? err.requestId : undefined;
  toast.error(errorMessage(err), {
    description: requestId ? t('errors.reference', { id: requestId.slice(-8) }) : undefined,
    action: requestId
      ? {
          label: (
            <span className="flex items-center gap-1">
              <Copy size={13} /> {t('errors.copyReference')}
            </span>
          ),
          onClick: () => {
            void navigator.clipboard
              .writeText(requestId)
              .then(() => toast.success(t('errors.referenceCopied')));
          },
        }
      : undefined,
    duration: 8000,
  });
}

/** One toast that follows an API call from start to finish. */
export function toastPromise<T>(
  promise: Promise<T>,
  messages: { loading: string; success: string | ((value: T) => string); description?: string },
): Promise<T> {
  const id = toast.loading(messages.loading);
  return promise.then(
    (value) => {
      toast.success(
        typeof messages.success === 'function' ? messages.success(value) : messages.success,
        {
          id,
          description: messages.description,
        },
      );
      return value;
    },
    (err: unknown) => {
      toast.dismiss(id);
      toastError(err);
      throw err;
    },
  );
}

export { toast };
